import type IMonitoringExecution from 'constants/interfaces/IMonitoringExecution';
import type {
  IMonitoringExecutionDetail,
  IMonitoringExecutionUnit,
  TMonitoringExecutionStatusRollup,
  TMonitoringExecutionTriggerType,
} from 'constants/interfaces/IMonitoringExecution';
import type IMonitoringRun from 'constants/interfaces/IMonitoringRun';
import type { TMonitoringRunStatus } from 'constants/interfaces/IMonitoringRun';
import type IMonitoringRunFindings from 'constants/interfaces/IMonitoringRunFindings';
import type { IMonitoringFinding } from 'constants/interfaces/IMonitoringRunFindings';
import type IMonitoringSchedule from 'constants/interfaces/IMonitoringSchedule';
import type { IMonitoringSchedulePayload, TImpactScenarioMinSeverity } from 'constants/interfaces/IMonitoringSchedule';

/** Nest `VCAgentScheduleSerializer` payload (before FE mapping). */
export interface IAgentScheduleApiItem {
  id: number | string,
  title: string,
  type: string,
  subType: string,
  agentConfigId?: number | null,
  unitIds: number[],
  entities?: {
    unitId: number,
    name: string | null,
  }[],
  schedule: IMonitoringSchedulePayload,
  nextRunAt?: string | null,
  lastRunAt?: string | null,
  lastRunStatus?: string | null,
  paused?: boolean,
  autoCreateImpactScenario?: boolean,
  impactScenarioMinSeverity?: TImpactScenarioMinSeverity | null,
}

/** Nest `VCAgentScheduleRunSerializer` payload (before FE mapping). */
export interface IAgentScheduleRunApiItem {
  id: number | string,
  scheduleId?: number | string | null,
  scheduledAt?: string | null,
  startedAt?: string | null,
  completedAt?: string | null,
  status: string,
  durationMs?: number | null,
}

export interface IAgentScheduleExecutionApiItem {
  executionKey: string,
  scheduledAt: string | Date,
  triggerType: string,
  unitCount: number,
  findingCount: number,
  highRiskCount: number,
  statusRollup: string,
}

export interface IAgentScheduleExecutionUnitApiItem {
  runId: number | string,
  unitId: number | null,
  unitName: string | null,
  status: string,
  findingCount: number,
  highRiskCount: number,
}

export interface IAgentScheduleExecutionDetailApiItem {
  executionKey: string,
  scheduledAt: string | Date,
  triggerType: string,
  units: IAgentScheduleExecutionUnitApiItem[],
}

export interface IAgentRunFindingApiItem {
  id: number | string,
  findingType: string,
  severity: string,
  title: string,
  description?: string | null,
  unitId?: number | null,
  observedAt?: string | Date | null,
  confidenceScore?: string | null,
  sourceUrl?: string | null,
  sourcesJson?: Record<string, unknown> | unknown[] | null,
  payloadJson: Record<string, unknown>,
  metadataJson?: Record<string, unknown> | null,
  createdAt: string | Date,
  impactScenarioId?: number | null,
}

export interface IAgentRunFindingsApiItem {
  runId: number | string,
  summary: Record<string, unknown> | null,
  findings: IAgentRunFindingApiItem[],
}

export interface IAgentScheduleRunNowApiItem {
  scheduleId: number | string,
  sessionId: number | string,
  executionKey: string,
  scheduledAt: string | Date,
  runIds: number[],
}

const toIsoString = (value: string | Date | null | undefined): string => {
  if (value == null) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  return value.toISOString();
};

const mapRunStatus = (status: string): TMonitoringRunStatus => {
  switch (status) {
    case 'in_progress':
      return 'running';
    case 'pending':
      return 'pending';
    case 'completed':
      return 'completed';
    case 'failed':
      return 'failed';
    case 'skipped':
      return 'skipped';
    case 'running':
      return 'running';
    default:
      return 'skipped';
  }
};

const mapTriggerType = (triggerType: string): TMonitoringExecutionTriggerType => (
  triggerType === 'scheduled' ? 'scheduled' : 'manual'
);

const mapStatusRollup = (statusRollup: string): TMonitoringExecutionStatusRollup => {
  switch (statusRollup) {
    case 'pending':
    case 'running':
    case 'completed':
    case 'failed':
    case 'partial':
      return statusRollup;
    default:
      return 'pending';
  }
};

export const mapAgentScheduleFromApi = (item: IAgentScheduleApiItem): IMonitoringSchedule => ({
  id: String(item.id),
  title: item.title,
  sessionType: item.type,
  subType: item.subType,
  agentConfigId: item.agentConfigId ?? null,
  unitIds: item.unitIds ?? [],
  entities: (item.entities ?? []).map((entity) => ({
    unitId: entity.unitId,
    name: entity.name ?? `Unit ${entity.unitId}`,
  })),
  schedule: item.schedule,
  nextRunAt: item.nextRunAt ?? undefined,
  lastRunAt: item.lastRunAt ?? undefined,
  lastRunStatus: item.lastRunStatus ?? undefined,
  paused: Boolean(item.paused),
  autoCreateImpactScenario: Boolean(item.autoCreateImpactScenario),
  impactScenarioMinSeverity: item.impactScenarioMinSeverity ?? null,
});

export const mapAgentScheduleRunFromApi = (item: IAgentScheduleRunApiItem): IMonitoringRun => ({
  id: String(item.id),
  scheduleId: item.scheduleId != null ? String(item.scheduleId) : '',
  scheduledAt: item.scheduledAt ?? '',
  startedAt: item.startedAt ?? undefined,
  status: mapRunStatus(item.status),
  entityCount: 0,
  alertCount: 0,
});

export const mapAgentScheduleExecutionFromApi = (
  item: IAgentScheduleExecutionApiItem,
): IMonitoringExecution => ({
  executionKey: item.executionKey,
  scheduledAt: toIsoString(item.scheduledAt),
  triggerType: mapTriggerType(item.triggerType),
  unitCount: item.unitCount ?? 0,
  findingCount: item.findingCount ?? 0,
  highRiskCount: item.highRiskCount ?? 0,
  statusRollup: mapStatusRollup(item.statusRollup),
});

export const mapAgentScheduleExecutionUnitFromApi = (
  item: IAgentScheduleExecutionUnitApiItem,
): IMonitoringExecutionUnit => ({
  runId: String(item.runId),
  unitId: item.unitId,
  unitName: item.unitName ?? (item.unitId != null ? `Unit ${item.unitId}` : '—'),
  status: item.status,
  findingCount: item.findingCount ?? 0,
  highRiskCount: item.highRiskCount ?? 0,
});

export const mapAgentScheduleExecutionDetailFromApi = (
  item: IAgentScheduleExecutionDetailApiItem,
): IMonitoringExecutionDetail => ({
  executionKey: item.executionKey,
  scheduledAt: toIsoString(item.scheduledAt),
  triggerType: mapTriggerType(item.triggerType),
  units: (item.units ?? []).map(mapAgentScheduleExecutionUnitFromApi),
});

export const mapAgentRunFindingFromApi = (item: IAgentRunFindingApiItem): IMonitoringFinding => ({
  id: String(item.id),
  findingType: item.findingType,
  severity: item.severity,
  title: item.title,
  description: item.description ?? null,
  unitId: item.unitId ?? null,
  observedAt: item.observedAt != null ? toIsoString(item.observedAt) : null,
  confidenceScore: item.confidenceScore ?? null,
  sourceUrl: item.sourceUrl ?? null,
  sourcesJson: item.sourcesJson ?? null,
  payloadJson: item.payloadJson ?? {},
  metadataJson: item.metadataJson ?? null,
  createdAt: toIsoString(item.createdAt),
  impactScenarioId: item.impactScenarioId ?? null,
});

export const mapAgentRunFindingsFromApi = (
  item: IAgentRunFindingsApiItem,
): IMonitoringRunFindings => ({
  runId: String(item.runId),
  summary: item.summary ?? null,
  findings: (item.findings ?? []).map(mapAgentRunFindingFromApi),
});
